import { useState } from 'react';
import { mensajeDeError } from '@/core/red/conectividad';
import {
  MOBILITY_LABELS,
  updateEstablishment,
  type Establishment,
  type MobilityMode,
  type OrganizationSettings,
} from './clients';
import type { Participant } from '@/features/works/works';

interface Props {
  establishment: Establishment;
  settings: OrganizationSettings | null;
  /** Para elegir quién cobra la movilidad de este campo. */
  participants: Participant[];
  onSaved: () => void;
}

const MODES: MobilityMode[] = ['none', 'per_km_fuel', 'per_km_fixed', 'flat', 'manual'];

/** Etiqueta que se muestra cuando el campo está en NULL: hereda del nivel superior. */
function heredado(valor: string | undefined) {
  return `Heredado de la organización${valor ? ` (${valor})` : ''}`;
}

/**
 * Configuración de movilidad por establecimiento.
 *
 * NULL significa HEREDAR, no "cero" (Spec §2.3). Por eso los selectores tienen
 * una opción explícita de herencia y el formulario la muestra siempre.
 */
export function EstablishmentEditor({ establishment, settings, participants, onSaved }: Props) {
  const [km, setKm] = useState(establishment.default_km?.toString() ?? '');
  const [beneficiario, setBeneficiario] = useState(
    establishment.default_mobility_participant_id ?? '',
  );
  const [charge, setCharge] = useState<'inherit' | 'yes' | 'no'>(
    establishment.charge_mobility === null
      ? 'inherit'
      : establishment.charge_mobility
        ? 'yes'
        : 'no',
  );
  const [mode, setMode] = useState<MobilityMode | 'inherit'>(
    establishment.mobility_mode ?? 'inherit',
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [ok, setOk] = useState(false);

  const save = async () => {
    setBusy(true);
    setError(null);
    setOk(false);
    try {
      await updateEstablishment(establishment.id, {
        default_km: km.trim() === '' ? null : Number(km),
        charge_mobility: charge === 'inherit' ? null : charge === 'yes',
        mobility_mode: mode === 'inherit' ? null : mode,
        default_mobility_participant_id: beneficiario === '' ? null : beneficiario,
      });
      setOk(true);
      onSaved();
    } catch (e) {
      setError(mensajeDeError(e));
    } finally {
      setBusy(false);
    }
  };

  const modoHeredado = settings ? MOBILITY_LABELS[settings.default_mobility_mode] : undefined;
  const cobroHeredado = settings ? (settings.charge_mobility_by_default ? 'Sí' : 'No') : undefined;

  return (
    <div
      className="flex flex-col gap-3 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] p-3"
      data-testid="establishment-editor"
    >
      <div className="flex items-center justify-between gap-2">
        <strong className="text-sm">{establishment.name}</strong>
        {establishment.record_kind === 'legacy_placeholder' && (
          <span className="rounded-[var(--radius-control)] bg-[var(--aviso-fondo)] px-2 py-0.5 text-xs text-[var(--aviso)]">
            legacy
          </span>
        )}
      </div>

      <label className="flex flex-col gap-1 text-sm">
        Kilómetros habituales
        <input
          type="number"
          min="0"
          step="0.01"
          value={km}
          onChange={(e) => setKm(e.target.value)}
          placeholder="Sin definir"
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-3"
          data-testid="est-km"
        />
        <span className="text-xs text-[var(--tinta-tenue)]">
          Se usa como sugerencia al cargar un trabajo. Se puede cambiar en cada trabajo.
        </span>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        ¿Se cobra movilidad?
        <select
          value={charge}
          onChange={(e) => setCharge(e.target.value as typeof charge)}
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
          data-testid="est-charge"
        >
          <option value="inherit">{heredado(cobroHeredado)}</option>
          <option value="yes">Sí</option>
          <option value="no">No</option>
        </select>
      </label>

      <label className="flex flex-col gap-1 text-sm">
        Método de movilidad
        <select
          value={mode}
          onChange={(e) => setMode(e.target.value as typeof mode)}
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
          data-testid="est-mode"
        >
          <option value="inherit">{heredado(modoHeredado)}</option>
          {MODES.map((m) => (
            <option key={m} value={m}>
              {MOBILITY_LABELS[m]}
            </option>
          ))}
        </select>
      </label>

      {/* Este campo no existía.
          La columna estaba en la base desde el principio y el formulario de
          trabajo la leía para proponer un beneficiario, pero no había ninguna
          pantalla donde cargarla: el valor era imposible de fijar y la
          sugerencia nunca aparecía. Se cargaba a mano en cada trabajo, uno por
          uno. */}
      <label className="flex flex-col gap-1 text-sm">
        ¿Quién cobra la movilidad de este campo?
        <select
          value={beneficiario}
          onChange={(e) => setBeneficiario(e.target.value)}
          className="min-h-11 rounded-[var(--radius-control)] border border-[var(--linea-fuerte)] px-2"
          data-testid="est-mobility-participant"
        >
          <option value="">Se elige en cada trabajo</option>
          {participants
            .filter((p) => p.is_active)
            .map((p) => (
              <option key={p.id} value={p.id}>
                {p.display_name}
              </option>
            ))}
        </select>
        <span className="text-xs text-[var(--tinta-tenue)]">
          Es una sugerencia al cargar un trabajo, no una regla: en el trabajo se puede cambiar.
        </span>
      </label>

      {error && <p className="text-sm text-[var(--peligro)]">{error}</p>}
      {ok && <p className="text-sm text-[var(--exito)]">Guardado</p>}

      <button
        type="button"
        onClick={() => void save()}
        disabled={busy}
        className="min-h-11 rounded-[var(--radius-control)] bg-[var(--accion)] px-4 text-sm text-[var(--sobre-accion)] disabled:opacity-50"
        data-testid="est-save"
      >
        {busy ? 'Guardando…' : 'Guardar establecimiento'}
      </button>
    </div>
  );
}
